/**
 * A queue that maintains only unique elements.
 * Duplicate elements are ignored when pushed.
 */
export default class DeDupQueue {
    queue = [];
    seen = new Set();
    waitResolvers = [];
    /**
     * Pushes an element to the queue if it's not already present.
     * If the element is a duplicate, it's ignored (noop).
     * @param id - The unique identifier for the item
     * @param data - The item to push to the queue
     * @returns true if the item was added, false if it was a duplicate
     */
    push(id, data) {
        if (this.seen.has(id)) {
            return false; // Duplicate item, ignore it
        }
        this.queue.push({ id, data });
        this.seen.add(id);
        // Resolve any waiting promises when an item is added
        this.resolveWaiters();
        return true;
    }
    /**
     * Removes and returns the first element from the queue.
     * @returns The first element or undefined if the queue is empty
     */
    pop() {
        const item = this.queue.shift();
        if (item !== undefined) {
            this.seen.delete(item.id);
        }
        return item?.data;
    }
    /**
     * Waits for the next item to become available in the queue.
     * If the queue already has items, resolves immediately.
     * If the queue is empty, resolves when a new item is added.
     * @returns A promise that resolves when an item is available
     */
    waitForNextItem() {
        // If there are already items in the queue, resolve immediately
        if (this.queue.length > 0) {
            return Promise.resolve();
        }
        // Otherwise, wait for a new item to be added
        return new Promise((resolve) => {
            this.waitResolvers.push(resolve);
        });
    }
    /**
     * Resolves all waiting promises when an item is added to the queue
     */
    resolveWaiters() {
        this.waitResolvers.forEach(resolve => resolve());
        this.waitResolvers = [];
    }
    /**
     * Gets the current size of the queue.
     * @returns The number of elements in the queue
     */
    get size() {
        return this.queue.length;
    }
    /**
     * Checks if the queue is empty.
     * @returns true if the queue is empty, false otherwise
     */
    get isEmpty() {
        return this.queue.length === 0;
    }
    /**
     * Clears all elements from the queue.
     */
    clear() {
        this.queue = [];
        this.seen.clear();
    }
}
